/**
 * Shopify Integration Factory
 * Factory class for creating and configuring Shopify integration instances
 */

import { ShopifyIntegration, ShopifyConfig } from './ShopifyIntegration';
import { IntegrationConfig } from '../../../types/integration';

export interface ShopifyConnectionParams {
  shopDomain: string;
  accessToken?: string;
  apiKey?: string;
  apiSecret?: string;
  webhookSecret?: string;
  scopes?: string[];
  usePrivateApp?: boolean;
  apiVersion?: string;
  webhookEndpoint?: string;
}

export class ShopifyIntegrationFactory {
  
  /**
   * Create a new Shopify integration instance
   */
  static create(params: ShopifyConnectionParams): ShopifyIntegration {
    const config: ShopifyConfig = {
      id: `shopify_${params.shopDomain}`,
      name: `Shopify - ${params.shopDomain}`,
      type: 'ecommerce',
      platform: 'shopify',
      status: 'inactive',
      shopDomain: params.shopDomain,
      accessToken: params.accessToken,
      apiKey: params.apiKey,
      apiSecret: params.apiSecret,
      webhookSecret: params.webhookSecret,
      scopes: params.scopes || [
        'read_products',
        'write_products',
        'read_customers',
        'write_customers',
        'read_orders',
        'write_orders',
        'read_inventory',
        'write_inventory',
        'read_checkouts',
        'write_checkouts'
      ],
      usePrivateApp: params.usePrivateApp || false,
      apiVersion: params.apiVersion || '2023-10',
      webhookEndpoint: params.webhookEndpoint,
      credentials: {
        encrypted: '',
        keyId: '',
        algorithm: 'aes-256-gcm',
        createdAt: new Date()
      },
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 250,
        rateLimitPerMinute: 120,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {}
      },
      fieldMappings: [],
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: 'UTC',
        batchSize: 250,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: [{
          type: 'info',
          message: 'Not yet connected',
          timestamp: new Date(),
          resolved: false
        }]
      },
      metadata: {
        description: 'Shopify e-commerce integration',
        tags: ['ecommerce', 'shopify', 'products', 'customers', 'orders'],
        category: 'E-commerce',
        version: '1.0.0',
        supportedFeatures: [
          'product_sync',
          'customer_sync',
          'order_sync',
          'cart_abandonment',
          'inventory_tracking',
          'webhooks'
        ],
        limitations: [
          'Rate limited to 2 requests per second',
          'Webhook delivery may have delays'
        ],
        documentation: 'https://shopify.dev/api/admin-rest',
        customData: {}
      }
    };

    return new ShopifyIntegration(config);
  }

  /**
   * Create Shopify integration from existing configuration
   */
  static fromConfig(config: IntegrationConfig): ShopifyIntegration {
    if (config.platform !== 'shopify') {
      throw new Error('Invalid platform for Shopify integration');
    }

    return new ShopifyIntegration(config as ShopifyConfig);
  }

  /**
   * Validate Shopify connection parameters
   */
  static validateParams(params: ShopifyConnectionParams): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate shop domain
    if (!params.shopDomain) {
      errors.push('Shop domain is required');
    } else if (!this.isValidShopDomain(params.shopDomain)) {
      errors.push('Invalid shop domain format');
    }

    // Validate authentication
    if (params.usePrivateApp) {
      if (!params.accessToken) {
        errors.push('Access token is required for private app');
      }
    } else {
      if (!params.apiKey || !params.apiSecret) {
        errors.push('API key and secret are required for OAuth app');
      }
    }

    // Validate API version
    if (params.apiVersion && !this.isValidApiVersion(params.apiVersion)) {
      errors.push('Invalid API version format');
    }

    // Validate scopes
    if (params.scopes && !this.areValidScopes(params.scopes)) {
      errors.push('Invalid scopes provided');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get default configuration for Shopify integration
   */
  static getDefaultConfig(): Partial<ShopifyConfig> {
    return {
      scopes: [
        'read_products',
        'write_products',
        'read_customers',
        'write_customers',
        'read_orders',
        'write_orders',
        'read_inventory',
        'write_inventory',
        'read_checkouts',
        'write_checkouts'
      ],
      usePrivateApp: false,
      apiVersion: '2023-10',
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 250,
        rateLimitPerMinute: 120,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {}
      },
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: 'UTC',
        batchSize: 250,
        conflictResolution: 'manual'
      }
    };
  }

  /**
   * Get required scopes for different features
   */
  static getRequiredScopes(features: string[]): string[] {
    const scopeMap: Record<string, string[]> = {
      products: ['read_products', 'write_products'],
      customers: ['read_customers', 'write_customers'],
      orders: ['read_orders', 'write_orders'],
      inventory: ['read_inventory', 'write_inventory'],
      checkouts: ['read_checkouts', 'write_checkouts'],
      analytics: ['read_analytics'],
      marketing: ['read_marketing_events', 'write_marketing_events'],
      fulfillments: ['read_fulfillments', 'write_fulfillments'],
      shipping: ['read_shipping', 'write_shipping']
    };

    const requiredScopes = new Set<string>();
    
    for (const feature of features) {
      const scopes = scopeMap[feature];
      if (scopes) {
        scopes.forEach(scope => requiredScopes.add(scope));
      }
    }

    return Array.from(requiredScopes);
  }

  /**
   * Create OAuth authorization URL
   */
  static createOAuthUrl(params: {
    shopDomain: string;
    apiKey: string;
    scopes: string[];
    redirectUri: string;
    state?: string;
  }): string {
    const { shopDomain, apiKey, scopes, redirectUri, state } = params;
    
    const baseUrl = `https://${shopDomain}.myshopify.com/admin/oauth/authorize`;
    const searchParams = new URLSearchParams({
      client_id: apiKey,
      scope: scopes.join(','),
      redirect_uri: redirectUri
    });

    if (state) {
      searchParams.set('state', state);
    }

    return `${baseUrl}?${searchParams.toString()}`;
  }

  /**
   * Exchange authorization code for access token
   */
  static async exchangeCodeForToken(params: {
    shopDomain: string;
    apiKey: string;
    apiSecret: string;
    code: string;
  }): Promise<{ accessToken: string; scope: string }> {
    const { shopDomain, apiKey, apiSecret, code } = params;
    
    const url = `https://${shopDomain}.myshopify.com/admin/oauth/access_token`;
    const body = {
      client_id: apiKey,
      client_secret: apiSecret,
      code
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`OAuth token exchange failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      accessToken: data.access_token,
      scope: data.scope
    };
  }

  // Private validation methods

  private static isValidShopDomain(domain: string): boolean {
    // Shopify domain should be alphanumeric with hyphens, no spaces
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9]$/;
    return domainRegex.test(domain) && domain.length >= 3 && domain.length <= 60;
  }

  private static isValidApiVersion(version: string): boolean {
    // API version should be in format YYYY-MM
    const versionRegex = /^\d{4}-\d{2}$/;
    return versionRegex.test(version);
  }

  private static areValidScopes(scopes: string[]): boolean {
    const validScopes = [
      'read_products',
      'write_products',
      'read_customers',
      'write_customers',
      'read_orders',
      'write_orders',
      'read_inventory',
      'write_inventory',
      'read_checkouts',
      'write_checkouts',
      'read_analytics',
      'read_marketing_events',
      'write_marketing_events',
      'read_fulfillments',
      'write_fulfillments',
      'read_shipping',
      'write_shipping',
      'read_content',
      'write_content',
      'read_themes',
      'write_themes',
      'read_script_tags',
      'write_script_tags',
      'read_files',
      'write_files'
    ];

    return scopes.every(scope => validScopes.includes(scope));
  }
}